// Smaller primitives: cards, section labels, avatars, empty/loading states,
// segmented tabs, and the reason list shared by pause/block/selection.

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export function SectionLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="sectionLabel" style={{ marginBottom: 8, ...style }}>
      {children}
    </div>
  );
}

export function Card({
  children,
  radius = 16,
  tone = 'surface',
  border,
  style,
}: {
  children: ReactNode;
  radius?: number;
  tone?: 'surface' | 'surface2' | 'accent';
  border?: string;
  style?: CSSProperties;
}) {
  const bg = tone === 'accent' ? 'var(--accent)' : tone === 'surface2' ? 'var(--surface2)' : 'var(--surface)';
  return (
    <div
      style={{
        background: bg,
        color: tone === 'accent' ? 'var(--accentInk)' : 'var(--ink)',
        borderRadius: radius,
        border: border ?? (tone === 'surface' ? '1px solid var(--line)' : 'none'),
        boxShadow: tone === 'surface' ? 'var(--elev)' : 'none',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Initials tile (square) or crew avatar (round). */
export function AvatarTile({
  name,
  size = 38,
  round = false,
  photoUrl,
  background,
  style,
}: {
  name: string;
  size?: number;
  round?: boolean;
  photoUrl?: string | undefined;
  background?: string;
  style?: CSSProperties;
}) {
  const base: CSSProperties = {
    width: size,
    height: size,
    borderRadius: round ? '50%' : Math.round(size * 0.32),
    display: 'grid',
    placeItems: 'center',
    flex: 'none',
    overflow: 'hidden',
    background: background ?? 'var(--accent)',
    color: 'var(--accentInk)',
    font: `700 ${Math.max(11, Math.round(size * 0.36))}px 'Space Grotesk',sans-serif`,
    ...style,
  };
  return <Avatar base={base} name={name} photoUrl={photoUrl} />;
}

/**
 * The roster hands every employee an avatar URL whether or not Odoo actually
 * holds a photo for them, so a good third of them resolve to nothing. Falling
 * back on error rather than trying to know in advance keeps this correct for
 * every reason the image can fail — no photo, no permission, a dropped
 * connection — instead of only the one we predicted.
 */
function Avatar({
  base,
  name,
  photoUrl,
}: {
  base: CSSProperties;
  name: string;
  photoUrl: string | undefined;
}) {
  const [failed, setFailed] = useState(false);
  // A new URL deserves a fresh attempt; without this, one failure would stick
  // to whichever person the tile is later reused for.
  useEffect(() => setFailed(false), [photoUrl]);

  if (!photoUrl || failed) return <div style={base}>{initials(name)}</div>;
  return (
    <div style={base}>
      <img
        src={photoUrl}
        alt=""
        onError={() => setFailed(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]![0] ?? '?').toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase();
}

export function EmptyState({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: 'center',
        color: 'var(--sub)',
        font: '500 12.5px Manrope,sans-serif',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Skeleton({ height = 64, radius = 13, style }: { height?: number; radius?: number; style?: CSSProperties }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height,
        borderRadius: radius,
        background: 'var(--surface2)',
        animation: 'ipPulse 1.6s infinite',
        ...style,
      }}
    />
  );
}

export function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} style={{ display: 'flex', gap: 8 }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            className="press"
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 12,
              font: '600 12.5px Manrope,sans-serif',
              background: active ? 'var(--accent)' : 'var(--surface2)',
              color: active ? 'var(--accentInk)' : 'var(--ink)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export interface ReasonItem {
  id: string | number;
  label: string;
  sub?: string;
  dot?: string;
}

/** Dot + label rows — pause reasons, block reasons, quick-complete reasons. */
export function ReasonList({
  items,
  onPick,
  selectedId,
}: {
  items: readonly ReasonItem[];
  onPick: (item: ReasonItem) => void;
  selectedId?: string | number | null;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item) => {
        const selected = selectedId != null && selectedId === item.id;
        return (
          <button
            key={item.id}
            type="button"
            className="press-row"
            onClick={() => onPick(item)}
            aria-pressed={selected}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '13px 14px',
              borderRadius: 13,
              background: selected ? 'var(--soft)' : 'var(--surface2)',
              border: `1.5px solid ${selected ? 'var(--accent)' : 'transparent'}`,
              width: '100%',
            }}
          >
            {item.dot && (
              <span
                style={{ width: 9, height: 9, borderRadius: '50%', background: item.dot, flex: 'none' }}
                aria-hidden="true"
              />
            )}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', font: '600 13.5px Manrope,sans-serif', color: 'var(--ink)' }}>
                {item.label}
              </span>
              {item.sub && (
                <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
                  {item.sub}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
